import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Anchor, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { API, LOGO, PHONE } from "@/lib/site";
import { getToken, setToken, clearToken, authHeader } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls =
  "bg-white/[0.03] border-white/15 rounded-none h-12 text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-gold focus-visible:border-gold";
const STATUSES = ["new", "contacted", "scheduled", "done", "archived"];
const STATUS_COLOR = {
  new: "text-gold border-gold/50",
  contacted: "text-blue-300 border-blue-300/40",
  scheduled: "text-purple-300 border-purple-300/40",
  done: "text-green-400 border-green-400/40",
  archived: "text-white/40 border-white/20",
};

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      setToken(data.access_token);
      toast.success("Welcome back.");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-black">
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        onSubmit={login}
        className="w-full max-w-md border border-white/10 bg-white/[0.02] p-10"
        data-testid="admin-login-form"
      >
        <div className="flex flex-col items-center text-center mb-8">
          <img src={LOGO} alt="Black Anchor Collective" className="h-16 w-16 object-contain" />
          <h1 className="mt-4 font-serif text-3xl text-white">Staff Login</h1>
          <p className="mt-1 text-xs uppercase tracking-[0.25em] text-gold">Black Anchor Collective</p>
        </div>
        <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-white/50">Email</label>
        <Input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="admin-email" />
        <label className="mt-5 mb-2 block text-xs uppercase tracking-[0.2em] text-white/50">Password</label>
        <Input className={inputCls} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="admin-password" />
        <button type="submit" disabled={loading} className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-gold text-black px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-gold-light transition-colors disabled:opacity-60" data-testid="admin-login-submit">
          {loading ? <><Loader2 size={16} className="animate-spin" /> Signing in</> : "Sign In"}
        </button>
        <Link to="/" className="mt-6 block text-center text-xs uppercase tracking-[0.2em] text-white/40 hover:text-gold transition-colors">
          ← Back to site
        </Link>
      </motion.form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("bookings");

  const load = async () => {
    setLoading(true);
    try {
      const [b, w] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers: authHeader() }),
        axios.get(`${API}/admin/waitlist`, { headers: authHeader() }),
      ]);
      setBookings(b.data);
      setWaitlist(w.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = () => {
    const headers = ["founder_number", "name", "email", "phone", "machine", "created_at"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = waitlist.map((w) => [w.founder_number || "", w.name, w.email, w.phone || "", w.message || "", w.created_at]);
    const csv = [headers.join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `founding-fleet-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${id}`, { status }, { headers: authHeader() });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: bookings.filter((b) => b.status === s).length }), {});

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/10 bg-black/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="logo" className="h-10 w-10 object-contain" />
            <div>
              <p className="font-serif tracking-[0.2em] text-white/90">BOOKINGS</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-gold">Black Anchor Collective</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={load} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 hover:text-gold transition-colors" data-testid="admin-refresh">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={onLogout} className="inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-white/80 hover:bg-white hover:text-black transition-colors" data-testid="admin-logout">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 md:px-10 py-10">
        <div className="mb-8 flex gap-2">
          <button
            onClick={() => setTab("bookings")}
            className={`px-5 py-2.5 text-xs uppercase tracking-[0.2em] border transition-colors ${tab === "bookings" ? "border-gold text-gold" : "border-white/15 text-white/50 hover:text-white"}`}
            data-testid="admin-tab-bookings"
          >
            Bookings ({bookings.length})
          </button>
          <button
            onClick={() => setTab("waitlist")}
            className={`px-5 py-2.5 text-xs uppercase tracking-[0.2em] border transition-colors ${tab === "waitlist" ? "border-gold text-gold" : "border-white/15 text-white/50 hover:text-white"}`}
            data-testid="admin-tab-waitlist"
          >
            Founding Fleet ({waitlist.length})
          </button>
          {tab === "waitlist" && waitlist.length > 0 && (
            <button
              onClick={exportCsv}
              className="ml-auto inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.2em] border border-white/15 text-white/70 hover:text-gold hover:border-gold transition-colors"
              data-testid="admin-export-csv"
            >
              <Download size={14} /> Export CSV
            </button>
          )}
        </div>

        {tab === "bookings" && (
        <>
        <div className="mb-8 flex flex-wrap gap-3">
          <span className="border border-gold/40 px-4 py-2 text-sm"><b className="text-gold">{bookings.length}</b> total</span>
          {STATUSES.map((s) => (
            <span key={s} className={`border px-4 py-2 text-sm capitalize ${STATUS_COLOR[s]}`}>{counts[s] || 0} {s}</span>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-white/50 py-20 justify-center"><Loader2 className="animate-spin" size={18} /> Loading…</div>
        ) : bookings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center border border-white/10">
            <Anchor size={32} className="text-gold mb-4" />
            <p className="text-white/60">No booking requests yet.</p>
          </div>
        ) : (
          <div className="border border-white/10 divide-y divide-white/10" data-testid="admin-bookings-list">
            {bookings.map((b) => (
              <div key={b.id} className="grid grid-cols-1 lg:grid-cols-12 gap-4 p-5 hover:bg-white/[0.02] transition-colors" data-testid={`admin-booking-${b.id}`}>
                <div className="lg:col-span-3">
                  <p className="font-serif text-xl text-white">{b.name}</p>
                  <a href={`tel:${b.phone}`} className="block text-sm text-gold hover:text-gold-light">{b.phone}</a>
                  <a href={`mailto:${b.email}`} className="block text-xs text-white/50 break-all hover:text-white">{b.email}</a>
                </div>
                <div className="lg:col-span-3">
                  <p className="text-white/90 text-sm">{b.service}</p>
                  <p className="text-white/40 text-xs mt-1">{b.boat_type || "—"}</p>
                </div>
                <div className="lg:col-span-3">
                  <p className="text-white/70 text-sm">{b.preferred_date || "No date"}</p>
                  <p className="text-white/40 text-xs mt-1">{b.preferred_time || "Any time"}</p>
                  {b.message && <p className="text-white/40 text-xs mt-2 italic line-clamp-2">"{b.message}"</p>}
                </div>
                <div className="lg:col-span-3 flex lg:justify-end items-start">
                  <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                    <SelectTrigger className={`w-40 rounded-none bg-transparent capitalize ${STATUS_COLOR[b.status] || "border-white/20 text-white"}`} data-testid={`admin-status-${b.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/15 text-white rounded-none">
                      {STATUSES.map((s) => (<SelectItem key={s} value={s} className="capitalize focus:bg-gold focus:text-black">{s}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="lg:col-span-12 text-[10px] uppercase tracking-[0.2em] text-white/25">
                  {new Date(b.created_at).toLocaleString()} · {PHONE}
                </div>
              </div>
            ))}
          </div>
        )}
        </>
        )}

        {tab === "waitlist" && (
          loading ? (
            <div className="flex items-center gap-2 text-white/50 py-20 justify-center"><Loader2 className="animate-spin" size={18} /> Loading…</div>
          ) : waitlist.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center border border-white/10">
              <Anchor size={32} className="text-gold mb-4" />
              <p className="text-white/60">No Founding Fleet claims yet.</p>
            </div>
          ) : (
            <div className="border border-white/10 divide-y divide-white/10" data-testid="admin-waitlist-list">
              {waitlist.map((w) => (
                <div key={w.id} className="grid grid-cols-1 lg:grid-cols-12 gap-4 p-5 hover:bg-white/[0.02] transition-colors" data-testid={`admin-waitlist-${w.id}`}>
                  <div className="lg:col-span-2">
                    <span className="font-serif text-3xl text-gold-gradient">{w.founder_number ? `#${String(w.founder_number).padStart(3, "0")}` : "—"}</span>
                  </div>
                  <div className="lg:col-span-3">
                    <p className="font-serif text-xl text-white">{w.name}</p>
                    <a href={`mailto:${w.email}`} className="block text-xs text-white/50 break-all hover:text-white">{w.email}</a>
                    {w.phone && <a href={`tel:${w.phone}`} className="block text-sm text-gold hover:text-gold-light">{w.phone}</a>}
                  </div>
                  <div className="lg:col-span-5">
                    <p className="text-white/70 text-sm italic">{w.message || "—"}</p>
                  </div>
                  <div className="lg:col-span-2 lg:text-right">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/25">{new Date(w.created_at).toLocaleDateString()}</p>
                    {w.founder_number && (
                      <a
                        href={`${API}/waitlist/${w.id}/certificate`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-gold hover:text-gold-light"
                        data-testid={`admin-cert-${w.id}`}
                      >
                        <Download size={12} /> Cert
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!getToken());
  const logout = () => { clearToken(); setAuthed(false); };
  return authed ? <Dashboard onLogout={logout} /> : <Login onSuccess={() => setAuthed(true)} />;
}
